import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '@/shared/api/apiClient';
import { safeErrorMessage } from '@/shared/utils/errors';
import { characterLabel } from '@/features/references/utils/referenceNotes';
import { logger } from '@/shared/utils/logger';
import {
  fetchReferenceReview,
  isLiveOp,
  newClientRequestId,
  regenerateCharacter,
  type ReferenceReview,
} from '@/features/references/services/referencesApi';

/** How often to re-read the review while an operation is still going. */
export const REVIEW_POLL_MS = 3000;
/** A failed poll is retried, backing off up to this. */
export const REVIEW_POLL_MAX_MS = 30_000;

const errorCode = (err: unknown): string | undefined =>
  err instanceof ApiError && err.detail && typeof err.detail === 'object'
    ? (err.detail as { error?: string }).error
    : undefined;

/**
 * Whether the same click may safely be sent again: we do not know that the server saw
 * it (no response at all, or a 5xx). Anything else is an answer, and an answer to THIS
 * request id is final — the engine returns the original, e.g. rejected, operation for a
 * repeated id, so retrying a refused click with the same id could never succeed.
 */
const isRetryableWithSameId = (err: unknown): boolean =>
  err instanceof ApiError && (err.status === 0 || err.status >= 500);

/* A dismissed failure must stay dismissed after a reload: the engine keeps reporting
   the last operation. Per-viewer convenience only, so every access is guarded. */
const dismissedKey = (jobId: string) => `valoi:reference-review-dismissed:${jobId}`;

function readDismissed(jobId: string): ReadonlySet<string> {
  try {
    const raw = window.localStorage.getItem(dismissedKey(jobId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeDismissed(jobId: string, ids: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(dismissedKey(jobId), JSON.stringify([...ids]));
  } catch {
    /* storage unavailable: the banner just comes back after a reload */
  }
}

export type UseReferenceReviewOptions = {
  jobId: string;
  /** Approves through the processing provider so the job's status updates everywhere. */
  approve: (jobId: string, reviewedVersions: Record<string, number>) => Promise<void>;
  pollMs?: number;
};

export function useReferenceReview({ jobId, approve, pollMs = REVIEW_POLL_MS }: UseReferenceReviewOptions) {
  const [review, setReview] = useState<ReferenceReview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [charErrors, setCharErrors] = useState<Record<string, string>>({});
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);
  const [staleNotice, setStaleNotice] = useState<string | null>(null);
  /* Bumped when a load finishes, success or failure, so the polling effect below
     re-arms either way: a failed poll leaves `review` untouched, and without this the
     chain of timeouts would simply end there. */
  const [loads, setLoads] = useState(0);
  const failures = useRef(0);
  /* A polite live-region message for the things a screen-reader user would otherwise
     only learn by re-reading the page. */
  const [announcement, setAnnouncement] = useState('');
  const knownVersions = useRef<Map<string, number> | null>(null);
  const [dismissedOps, setDismissedOps] = useState<ReadonlySet<string>>(() => readDismissed(jobId));

  const mounted = useRef(true);
  /* Synchronous double-click guards. State is not enough: two clicks in one tick
     both read the same `false`. */
  const busy = useRef(false);
  const approvingRef = useRef(false);
  /* The id of the click in flight (or last failed retryably), per character, with the
     notes it was made for. Same notes → same id → the engine treats the retry as the
     original; edited notes are a different request and get a fresh id. */
  const attempts = useRef(new Map<string, { notes: string; id: string }>());

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const next = await fetchReferenceReview(jobId);
      if (!mounted.current) return;
      failures.current = 0;
      /* A character whose version moved while we were watching has a new set of
         pictures: say so, once. The first load only records what is there. */
      const seen = knownVersions.current;
      knownVersions.current = new Map(next.characters.map((c) => [c.charKey, c.version]));
      if (seen) {
        const updated = next.characters.filter((c) => (seen.get(c.charKey) ?? c.version) < c.version);
        if (updated.length > 0) {
          const names = updated.map((c) => c.name ?? characterLabel(c.charKey)).join(', ');
          setAnnouncement(`New character images are ready for ${names}. Review them, then approve.`);
        }
      }
      setReview(next);
      setLoadError(null);
    } catch (err) {
      if (!mounted.current) return;
      failures.current += 1;
      logger.error('Failed to load the reference review', err);
      setLoadError(safeErrorMessage(err, 'Could not load the character review. Please try again.'));
    } finally {
      if (mounted.current) setLoads((n) => n + 1);
    }
  }, [jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const polling = review?.characters.some((c) => isLiveOp(c.activeOp)) ?? false;
  useEffect(() => {
    if (!polling) return;
    /* After a failed poll keep trying, a little slower each time, instead of going quiet
       with a half-stale panel; "Try again" also works and resets the backoff. */
    const delay = Math.min(pollMs * 2 ** failures.current, REVIEW_POLL_MAX_MS);
    const timer = setTimeout(() => void load(), delay);
    return () => clearTimeout(timer);
  }, [polling, loads, load, pollMs]);

  const setCharError = (charKey: string, message: string | null) =>
    setCharErrors((prev) => {
      const next = { ...prev };
      if (message) next[charKey] = message;
      else delete next[charKey];
      return next;
    });

  const regenerate = useCallback(
    async (charKey: string, rawNotes: string) => {
      const notes = rawNotes.trim();
      if (busy.current || !notes) return;
      busy.current = true;
      const previous = attempts.current.get(charKey);
      const id = previous && previous.notes === notes ? previous.id : newClientRequestId();
      attempts.current.set(charKey, { notes, id });
      setSubmitting(charKey);
      setCharError(charKey, null);
      try {
        await regenerateCharacter(jobId, charKey, notes, id);
        attempts.current.delete(charKey);
        await load();
      } catch (err) {
        if (!isRetryableWithSameId(err)) attempts.current.delete(charKey);
        logger.error('Failed to regenerate a character', err);
        if (mounted.current) {
          setCharError(
            charKey,
            safeErrorMessage(err, 'Could not regenerate this character. Please try again.'),
          );
        }
        /* A 409 means our picture of the review is out of date (someone else, another
           tab, or an operation we missed) — re-read it so the card shows the truth. */
        if (err instanceof ApiError && err.status === 409) await load();
      } finally {
        busy.current = false;
        if (mounted.current) setSubmitting(null);
      }
    },
    [jobId, load],
  );

  const approveAll = useCallback(async () => {
    if (!review || approvingRef.current) return;
    approvingRef.current = true;
    setApproving(true);
    setApproveError(null);
    setStaleNotice(null);
    const reviewed = Object.fromEntries(review.characters.map((c) => [c.charKey, c.version]));
    try {
      await approve(jobId, reviewed);
    } catch (err) {
      logger.error('Failed to approve the character references', err);
      if (!mounted.current) return;
      const code = errorCode(err);
      if (code === 'references_changed') {
        /* Never approve the new versions on the user's behalf: show them what
           changed and let them decide. */
        await load();
        if (mounted.current) {
          setStaleNotice(
            'These characters changed since you last looked. Check the updated images, then approve again.',
          );
        }
      } else {
        if (err instanceof ApiError && err.status === 409) await load();
        if (mounted.current) {
          setApproveError(safeErrorMessage(err, 'Could not approve the characters. Please try again.'));
        }
      }
    } finally {
      approvingRef.current = false;
      if (mounted.current) setApproving(false);
    }
  }, [approve, jobId, load, review]);

  const dismissOp = useCallback(
    (opId: string) => {
      setDismissedOps((prev) => {
        const next = new Set(prev).add(opId);
        writeDismissed(jobId, next);
        return next;
      });
    },
    [jobId],
  );

  return {
    announcement,
    review,
    loading: review === null && loadError === null,
    loadError,
    reload: load,
    regenerate,
    submitting,
    charErrors,
    approve: approveAll,
    approving,
    approveError,
    staleNotice,
    dismissedOps,
    dismissOp,
  };
}
